import React, { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../../helpers/react-select-styles'
import { DateFormat, CurrencyFormat, PercentFormat, formatApr } from '../../../../helpers/formatting'
import { useSelector } from 'react-redux'
import { selectDropdownOptions } from '../../../../redux/dropdownSlice'
import PaymentDatePicker from "../../../../components/PaymentDatePicker";
import { format } from 'date-fns';
import Instruction from '../../../../components/elements/Instruction'

export default function CollectionRefinanceFields({
  control,
  refinanceInfo,
  payFrequencyOptions,
  setPayFrequencyValue,
  setPayFrequencyValueOption,
  watchDesiredAmount,
  today,
  collectionRefiMinMax,
  collectionRefiRates,
  getCollectionRefiMinMax,
  loanId,
  isMaxMinProcessing,
  setOfferSelection,
  offerSelection,
  collectionsFirstPaymentDate,
  setCollectionsFirstPaymentDate,
  cancelCollectionRefinance,
  displayFrequencyWarning,
}) {
  const dropdownOptions = useSelector(selectDropdownOptions);
  const minDesiredAmount = collectionRefiMinMax?.min ?? 0;
  const maxDesiredAmount = collectionRefiMinMax?.max ?? 0;
  const collectionRefinanceReason = dropdownOptions.find((o) => o?.code_key === 'refinance_reason_collections');

  const clickEventRef = useRef(null);

  if (refinanceInfo && refinanceInfo?.collection_refi_lead_id == null) {
    return (
      <Instruction content={"Something went wrong. Please return to the payment schedule screen and click on the Refinance - Collection quick action."} />
    )
  }

  return (
    <>
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="email_address">Refinance Reason:</label>
        <div className="control">{collectionRefinanceReason?.name}</div>
        <Controller
          name="refinance_reason_dropdown_option_id"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="hidden"
              value={collectionRefinanceReason?.id}
            />
          )}
        />
      </div>

      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="email_address">Current Loan Balance:</label>
        <div className="control"><CurrencyFormat value={+refinanceInfo?.payoff_amount} /></div>
      </div>

      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="email_address">Discount Savings:</label>
        <div className="control"><CurrencyFormat value={+refinanceInfo?.payoff_amounts?.interest + +refinanceInfo?.payoff_amounts?.loan_fee + +refinanceInfo?.payoff_amounts?.nsf_fee + +refinanceInfo?.payoff_amounts?.late_fee} /></div>
      </div>

      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="email_address">Refinanced Balance:</label>
        <div className="control"><CurrencyFormat value={+refinanceInfo?.payoff_amounts?.principal} /></div>
      </div>

      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`}>New Loan Effective Date:</label>
        <div className="control"><DateFormat dateString={today} /></div>
      </div>

      {/* Pay Frequency */}
      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="pay_frequency_dropdown_option_id">Pay Frequency:<span className="has-text-danger">*</span>
        </label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="pay_frequency_dropdown_option_id"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                {...field}
                classNames={reactSelectClassNames()}
                options={payFrequencyOptions}
                isSearchable={false}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  menu: ({ ...css }) => ({ ...css, width: '100%' }),
                }}
                menuPosition="fixed"
                onChange={(val) => {
                  setPayFrequencyValue(val?.label);
                  setPayFrequencyValueOption("");
                  field.onChange({ label: val?.label, value: val?.value });
                  cancelCollectionRefinance();
                  getCollectionRefiMinMax(loanId, val?.value);
                }}
                isDisabled={isMaxMinProcessing}
              />
            )}
          />
        </div>
      </div>

      {displayFrequencyWarning ? (
        <div className="field is-horizontal">
          <div className={`notification is-warning is-light p-1`}>
            <span>No plan can be found for this pay frequency. Please select a different frequency.</span>
          </div>
        </div>
      ) : null}

      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="next_pay_date">Holiday or Weekend Payday:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="is_pay_before_holiday"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                {...field}
                classNames={reactSelectClassNames()}
                options={[{ label: 'Before', value: 1 }, { label: 'After', value: 0 }]}
                isSearchable={false}
                onChange={(val) => {
                  field.onChange({ label: val?.label, value: val?.value });
                  cancelCollectionRefinance();
                }}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  menu: ({ ...css }) => ({ ...css, width: '100%' }),
                }}
                menuPosition="fixed"
              />
            )}
          />
        </div>
      </div>

      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="next_pay_date">First Payment Date:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="first_payment_date"
            control={control}
            render={({ field }) => (
              <PaymentDatePicker
                {...field}
                paymentMethodDdoId={refinanceInfo?.active_loan?.payment_method_dropdown_option_id}
                min={`${refinanceInfo?.min_first_payment_date}T00:00:00`}
                max={`${refinanceInfo?.max_first_payment_date}T00:00:00`}
                selectedDate={collectionsFirstPaymentDate}
                // setSelectedDate={setCollectionsFirstPaymentDate}
                setSelectedDate={(date) => {
                  setCollectionsFirstPaymentDate(date);
                  field.onChange(date);
                  cancelCollectionRefinance();
                  getCollectionRefiMinMax(loanId, null, format(date, 'yyyy-MM-dd'));
                }}
                isDisabled={isMaxMinProcessing}
              />
            )}
          />
        </div>
      </div>

      <div className="field is-horizontal">
        <label className={`refinance-loan-modal-label`} htmlFor="additional_funds">Max Desired Payment Amount:<span className="has-text-danger">*</span></label>
        <div className="control refinance-loan-modal-fields">
          <Controller
            name="desired_amount"
            control={control}
            defaultValue={0}
            render={({ field }) => (
              <input
                {...field}
                type="number"
                className="input is-small"
                min={minDesiredAmount}
                max={maxDesiredAmount}
                step="0.01"
                onChange={(e) => {
                  // Allow any value while typing
                  field.onChange(e.target.value);
                  cancelCollectionRefinance();
                }}
                disabled={isMaxMinProcessing}
              />
            )}
          />
          <div><small className={(watchDesiredAmount < minDesiredAmount || watchDesiredAmount > maxDesiredAmount) ? 'has-text-danger' : ''}>
            Min: <CurrencyFormat value={minDesiredAmount} /> - Max: <CurrencyFormat value={maxDesiredAmount} />
          </small></div>
        </div>
      </div>

      {/* Refi Offers */}
      {collectionRefiRates.length > 0 ? (
        <>
          <div className="field is-horizontal">
            <label className="refinance-loan-modal-label" htmlFor="offers">
              Offer(s):
            </label>
          </div>

          <div id="offers" className="field is-horizontal is-variable is-2 is-multiline">
            {collectionRefiRates.map((rate, index) => (
              <div key={rate.term_count} className="is-full-mobile is-one-third-tablet">
                <div
                  className={
                    "box offer-card " + (index === offerSelection ? "is-selected" : "")
                  }
                  onClick={async (e) => {
                    clickEventRef.current = e;
                    setOfferSelection(index);
                  }}
                >
                  <div className={"offer-card__title " + (index === offerSelection ? "is-selected" : "")}>
                    {rate.title ?? `Option ${index + 1}`}
                  </div>

                  <ul className="offer-card__list">
                    <li>
                      <span>Payments:</span> {rate.term_count}
                    </li>
                    <li>
                      <span>Amount:</span> <CurrencyFormat value={rate.payment_amount} />
                    </li>
                    <li>
                      <span>APR:</span> {formatApr(rate.apr)}%
                    </li>
                    <li className="offer-card__total">
                      <span>Total Interest:</span> <CurrencyFormat value={rate.total_interest} />
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </>
  )
}
